import { useState } from 'react'
import { CalendarioSemana } from './calendario/CalendarioSemana'
import { CalendarioMes } from './calendario/CalendarioMes'
import { PanelDia } from './calendario/PanelDia'
import { useCalendarioSemana, type DiaSemana } from '../hooks/useCalendarioSemana'
import { useCalendarioMes } from '../hooks/useCalendarioMes'
import { colorDeDia } from './agendamiento/coloresDia'
import type { DiaRuta } from '../hooks/useFiltrosAgendamiento'
import { Cargando } from '../componentes/Cargando'
import { EstadoError } from '../componentes/EstadoError'

/**
 * Calendario de repartos, con dos vistas: MES (rejilla tipo HEKKIO, por defecto)
 * y SEMANA (B.1). El mes es la vista de referencia pedida: rejilla con píldoras
 * (fondo=ruta, marca=estado) y leyenda; al pulsar un día se abre el panel del
 * día para ver los repartos y añadir uno nuevo (opción "ver + añadir").
 */

// lunes..viernes -> r1..r5; el fin de semana no tiene ruta de reparto (neutro).
const DIA_A_RUTA: Partial<Record<DiaSemana, DiaRuta>> = {
  lunes: 'r1',
  martes: 'r2',
  miercoles: 'r3',
  jueves: 'r4',
  viernes: 'r5',
}

function colorDeDiaSemana(dia: DiaSemana): string {
  const ruta = DIA_A_RUTA[dia]
  return ruta ? colorDeDia(ruta).clase : ''
}

type Vista = 'mes' | 'semana'

export function Calendario() {
  const [vista, setVista] = useState<Vista>('mes')
  const hoy = new Date()
  const [anio, setAnio] = useState(hoy.getFullYear())
  const [mes, setMes] = useState(hoy.getMonth())
  const [diaAbierto, setDiaAbierto] = useState<string | null>(null)

  return (
    <section className="flex flex-col gap-4 p-4">
      {/* Toggle mes/semana, como el conmutador de vistas de la referencia. */}
      <div className="flex items-center gap-1">
        <BotonVista activa={vista === 'mes'} onClick={() => setVista('mes')}>Mes</BotonVista>
        <BotonVista activa={vista === 'semana'} onClick={() => setVista('semana')}>Semana</BotonVista>
      </div>

      {vista === 'mes' ? (
        <VistaMes
          anio={anio}
          mes={mes}
          diaAbierto={diaAbierto}
          onCambiarMes={(a, m) => { setAnio(a); setMes(m); setDiaAbierto(null) }}
          onSeleccionarDia={setDiaAbierto}
          onCerrarDia={() => setDiaAbierto(null)}
          onAnadir={(iso) => {
            // TODO(B.3): abrir el alta de reparto para `iso`.
            console.info('Añadir reparto el', iso)
          }}
        />
      ) : (
        <VistaSemana />
      )}
    </section>
  )
}

function BotonVista({ activa, onClick, children }: { activa: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={activa}
      className={[
        'rounded-md px-3 py-1.5 text-sm font-medium ring-1 transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-foco',
        activa ? 'bg-acento text-acento-contraste ring-transparent' : 'text-tinta-2 ring-borde hover:bg-superficie',
      ].join(' ')}
    >
      {children}
    </button>
  )
}

function VistaMes({
  anio, mes, diaAbierto, onCambiarMes, onSeleccionarDia, onCerrarDia, onAnadir,
}: {
  anio: number
  mes: number
  diaAbierto: string | null
  onCambiarMes: (anio: number, mes: number) => void
  onSeleccionarDia: (iso: string) => void
  onCerrarDia: () => void
  onAnadir: (iso: string) => void
}) {
  const mesId = `${anio}-${String(mes + 1).padStart(2, '0')}`
  const { data, isPending, isError, error, refetch } = useCalendarioMes(mesId)

  if (isPending) return <Cargando mensaje="Cargando el mes" />
  if (isError) return <EstadoError error={error} onReintentar={() => refetch()} />

  return (
    <div className="grid gap-4 lg:grid-cols-[1fr_20rem]">
      <CalendarioMes
        repartosPorFecha={data}
        anio={anio}
        mes={mes}
        onCambiarMes={onCambiarMes}
        onSeleccionarDia={onSeleccionarDia}
      />
      {diaAbierto ? (
        <PanelDia
          iso={diaAbierto}
          repartos={data[diaAbierto] ?? []}
          onAnadir={onAnadir}
          onCerrar={onCerrarDia}
        />
      ) : null}
    </div>
  )
}

function VistaSemana() {
  const { data, isPending, isError, error, refetch } = useCalendarioSemana()

  if (isPending) return <Cargando mensaje="Cargando la semana" />
  if (isError) return <EstadoError error={error} onReintentar={() => refetch()} />

  return (
    <CalendarioSemana
      repartosPorDia={data}
      colorDeDia={colorDeDiaSemana}
      tituloSemana="Repartos de la semana"
      onSeleccionarDia={(dia) => {
        console.info('Seleccionado dia para nuevo reparto:', dia)
      }}
    />
  )
}
